import React from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";

const HeroSection = styled.section`
  height: 600px;
  background: linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url("/images/slide_7.webp");
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  color: white;

  .hero-content {
    max-width: 700px;

    .tagline {
      font-size: 1.3rem;
      color: #ff6600;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 2px;
      margin-bottom: 1rem;
    }

    h1 {
      font-size: 3rem;
      margin-bottom: 1rem;
      color: white;
    }

    p {
      font-size: 1.2rem;
      margin-bottom: 2rem;
      line-height: 1.6;
      color: #e0e0e0;
    }

    .hero-buttons {
      display: flex;
      gap: 1rem;
      flex-wrap: wrap;
    }
  }

  @media (max-width: 768px) {
    height: 500px;

    .hero-content {
      h1 {
        font-size: 2rem;
      }

      p {
        font-size: 1rem;
      }

      .hero-buttons {
        flex-direction: column;
        align-items: flex-start;
      }
    }
  }
`;

const WelcomeSection = styled.section`
  padding: 80px 0;
  background: #0a0a0a;

  .container {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 4rem;
    align-items: center;
  }

  .welcome-content {
    h1 {
      color: white;
      span {
        color: #b0b0b0;
        font-weight: 400;
      }
    }

    p {
      color: #b0b0b0;
      margin-bottom: 1rem;
    }

    .read-more {
      color: #ff6600;
      font-weight: 500;
      text-decoration: underline;
    }
  }

  .welcome-image {
    img {
      width: 100%;
      height: auto;
      border-radius: 10px;
    }
  }

  @media (max-width: 768px) {
    .container {
      grid-template-columns: 1fr;
      gap: 2rem;
    }
  }
`;

const WhyChooseSection = styled.section`
  padding: 80px 0;
  background: #111;

  .section-title {
    text-align: center;
    margin-bottom: 3rem;

    h2 {
      font-size: 2.5rem;
      color: #ff6600;
    }
  }

  .features-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 2rem;
  }

  .feature-card {
    background: #1a1a1a;
    padding: 2rem;
    text-align: center;
    border-radius: 10px;
    border: 1px solid #333;
    transition: all 0.3s ease;

    &:hover {
      border-color: #ff6600;
      box-shadow: 0 5px 20px rgba(255, 102, 0, 0.2);
    }

    .feature-icon {
      width: 90px;
      height: 90px;
      margin: 0 auto 1.5rem;

      img {
        width: 100%;
        height: 100%;
        object-fit: contain;
      }
    }

    h3 {
      color: #ff6600;
      font-size: 1.2rem;
    }
  }
`;

const ServicesSection = styled.section`
  padding: 80px 0;
  background: #0a0a0a;

  .section-title {
    text-align: center;
    margin-bottom: 3rem;

    h2 {
      font-size: 2.5rem;
      color: #ff6600;
    }

    p {
      color: #b0b0b0;
      max-width: 800px;
      margin: 0 auto;
    }
  }

  .services-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 2rem;
  }

  .service-card {
    background: #1a1a1a;
    border-radius: 10px;
    overflow: hidden;
    border: 1px solid #333;
    transition: all 0.3s ease;
    position: relative;
    height: 300px;

    &:hover {
      border-color: #ff6600;

      .service-overlay {
        opacity: 1;
      }
    }

    .service-bg {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .service-overlay {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(rgba(255, 102, 0, 0.9), rgba(204, 82, 0, 0.9));
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      text-align: center;
      padding: 2rem;
      opacity: 0;
      transition: opacity 0.3s ease;
      color: white;

      h3 {
        color: white;
        margin-bottom: 1rem;
      }

      p {
        margin-bottom: 1.5rem;
      }

      .btn {
        background: white;
        color: #ff6600;

        &:hover {
          background: #f8f9fa;
        }
      }
    }

    .service-title {
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      background: linear-gradient(transparent, rgba(0, 0, 0, 0.8));
      padding: 2rem 1rem 1rem;
      color: white;

      h3 {
        color: white;
      }
    }
  }
`;

const Home = () => {
  const features = [
    {
      icon: "/images/safe_icon.png",
      title: "Reliable",
    },
    {
      icon: "/images/price_icon.png",
      title: "Best Prices",
    },
    {
      icon: "/images/bserv_icon.png",
      title: "Best Services",
    },
    {
      icon: "/images/avail_icon.png",
      title: "24/7 Available",
    },
  ];

  const services = [
    {
      title: "Mobile Fleet Tyre Fitting",
      description:
        "Our expert technicians specialise in on-site tyre fitting for heavy vehicles and roadtrains — depots, yards or roadside. Fast, safe and compliant with heavy-vehicle standards.",
      image: "/images/ser_mtf.webp",
      link: "/services#mobile-fitting",
    },

    {
      title: "Heavy-Duty New Tyres",
      description:
        "New commercial-grade tyres optimised for long-haul performance, high mileage and heavy loads. Sourced from trusted manufacturers for truck work.",
      image: "/images/ser_rnt.webp",
      link: "/services#new-tyres",
    },
    {
      title: "Truck Tyre Repair",
      description:
        "Specialist repair and assessment for truck and trailer tyres. Repairs adhere to industry safety guidelines to keep your fleet moving.",
      image: "/images/ser_tpr.webp",
      link: "/services#puncture-repair",
    },
    {
      title: "Wheel Rotation & Alignment",
      description: "Rotation and alignment services tailored for prime movers and trailers to ensure even wear and improve tyre life across axles.",
      image: "/images/ser_tirot.webp",
      link: "/services#tyre-rotation",
    },
    {
      title: "Heavy Vehicle Breakdown Support",
      description: "24/7 emergency support for heavy vehicles and roadtrains — mobile tyre change and on-road assistance to minimize downtime.",
      image: "/images/24breakdown-cover.webp",
      link: "/services#breakdown-cover",
    },
    // Trailer Repair entry removed (service not offered)
  ];

  return (
    <>
      <HeroSection>
        <div className="container">
          <div className="hero-content">
            <div className="tagline">Built for the long haul</div>
            <h1>Truck & Roadtrain Tyres</h1>
            <p>
              Road Trains consist of a prime mover pulling two or more trailer rigs connected by a converter dolly. We provide comprehensive tyre and
              truck solutions for all your transport needs.
            </p>
            <div className="hero-buttons">
              <Link
                to="/contact"
                className="btn">
                Contact Us
              </Link>
              <Link
                to="/get-quote"
                className="btn btn-outline">
                Get a Quote
              </Link>
            </div>
          </div>
        </div>
      </HeroSection>

      <WelcomeSection>
        <div className="container">
          <div className="welcome-content">
            <h1>
              <span>Welcome to</span>
              <br />
              Roadwise Tyres
            </h1>
            <p>
              Roadwise Tyres specialise in commercial truck tyres for long-haul, regional and roadtrain operations. We're not a consumer-focused shop
              — our products, inventory and technicians are all set up to support heavy vehicles and fleet requirements.
            </p>
            <p>
              We work with multiple commercial tyre brands to provide availability and choice for operators. Whether you run a single prime mover or a
              roadtrain fleet, we'll help you select tyres optimised for load, speed rating and long-distance durability.
            </p>
            <p>
              Our team provides depot fitting, roadside support and fleet servicing solutions designed to reduce downtime and keep your trucks moving.
            </p>
            {/* read more removed */}
          </div>

          <div className="welcome-image">
            <img
              src="/images/welc_pic.webp"
              alt="Welcome"
            />
          </div>
        </div>
      </WelcomeSection>

      <WhyChooseSection>
        <div className="container">
          <div className="section-title">
            <h2>Why Choose Us</h2>
          </div>

          <div className="features-grid">
            {features.map((feature, index) => (
              <div
                key={index}
                className="feature-card">
                <div className="feature-icon">
                  <img
                    src={feature.icon}
                    alt={feature.title}
                  />
                </div>
                <h3>{feature.title}</h3>
              </div>
            ))}
          </div>
        </div>
      </WhyChooseSection>

      <ServicesSection>
        <div className="container">
          <div className="section-title">
            <h2>Our Services</h2>
            <p>
              In today's fast-paced world, not only is tyre technology rapidly evolving, but there is an ever-increasing number of high-quality
              alternatives to traditional brands available.
            </p>
          </div>

          <div className="services-grid">
            {services.map((service, index) => (
              <div
                key={index}
                className="service-card">
                <img
                  src={service.image}
                  alt={service.title}
                  className="service-bg"
                />
                <div className="service-overlay">
                  <h3>{service.title}</h3>
                  <p>{service.description}</p>
                </div>
                <div className="service-title">
                  <h3>{service.title}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </ServicesSection>
    </>
  );
};

export default Home;
